import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';

import {EXTERNAL_FILE_TYPE} from 'constants/PreviewTypeConstants';
import {isS3URLExpired} from 'external-files/SignedExtFileURLProxy';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {type NormalizedRun} from 'model-hub/prediction-runs/splitPredictionRuns';
import StructurePredictionInfo from 'model-hub/scientific-models/structure-prediction/StructurePredictionInfo';
import {STICKY_FOOTER_CONTAINER_STYLES, STICKY_FOOTER_CONTENT_STYLES} from 'model-hub/StickyFooter';
import MolstarViewer, {MOLSTAR_PREVIEW_TYPE_TO_FORMAT} from 'molbio/molstar/ViewerWrapper';
import {LoadingFileScreen} from 'nav/full-screen/LoadingFullScreen';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import lazyInit from 'util/lazyInit';

import {type GenerativePredictionJobDetailQuery} from './__generated/GenerativePredictionJobDetailQuery';
import {GENERATIVE_PREDICTION_JOB_DETAIL_QUERY} from './GenerativeModelsQueries';

const getCachedSignedURLState = lazyInit(() =>
  makeUseGlobalClientState<string | undefined | null>(undefined)
);

function DownloadZippedResultButton({run}: {run: NormalizedRun}): JSX.Element {
  const [requesting, setRequesting] = useState(false);

  const onClick = async (): Promise<void> => {
    setRequesting(true);
    try {
      const result = await doQuerySafe<GenerativePredictionJobDetailQuery>(
        {query: GENERATIVE_PREDICTION_JOB_DETAIL_QUERY},
        {
          variables: {
            id: run.id,
          },
          fetchPolicy: 'no-cache',
        }
      );
      const signedUrl = result.predictionJob?.resultsDownloadUrl;
      if (signedUrl) {
        window.open(signedUrl, '_blank', 'noopener noreferrer');
      }
    } finally {
      setRequesting(false);
    }
  };

  return (
    <Button
      buttonStyle={ButtonStyle.PRIMARY}
      size={ButtonSize.SMALL}
      icon="download"
      text={requesting ? 'Downloading...' : 'Download generated structures'}
      onClick={onClick}
      disabled={requesting}
    />
  );
}

function StructureFileViewer({
  signedUrl,
  loading,
}: {
  signedUrl?: string | null | undefined;
  loading: boolean;
}): JSX.Element {
  if (loading) {
    return <LoadingFileScreen />;
  }
  if (!signedUrl) {
    return (
      <InlineBanner
        bannerType={InlineBannerType.ERROR}
        text="Failed to load generative model structure file."
      />
    );
  }
  return (
    <div className={css(styles.viewerWrapper)}>
      <MolstarViewer
        url={signedUrl}
        format={MOLSTAR_PREVIEW_TYPE_TO_FORMAT[EXTERNAL_FILE_TYPE.MOLSTAR_MMCIF_VIEWER]}
        className={classNames('reset', css(styles.viewer))}
        surfaceView={true}
      />
    </div>
  );
}

export default function GenerativeModelRunDetails({
  run,
  navigateToList,
}: {
  run: NormalizedRun;
  navigateToList: () => void;
}): JSX.Element {
  // Track when we last fetched data from the network (not from cache)
  const cachedSignedURLState = getCachedSignedURLState();
  const [signedURL, setSignedURL] = cachedSignedURLState.useGlobalClientState(
    `${run.id}_top_predicted_structure`
  );

  const queryResult = useBenchlingQuery<GenerativePredictionJobDetailQuery>(
    GENERATIVE_PREDICTION_JOB_DETAIL_QUERY,
    {
      variables: {
        id: run.id,
      },
      fetchPolicy: signedURL == null || isS3URLExpired(signedURL) ? 'network-only' : 'cache-first',
      onCompleted: (data) => {
        setSignedURL(data.predictionJob?.standardizedOutputDownloadUrl);
      },
    }
  );

  if (queryResult.type === QueryResultType.ERROR) {
    return (
      <InlineBanner
        bannerType={InlineBannerType.ERROR}
        text="An error occurred while loading the BoltzGen structure file."
      />
    );
  }
  const signedUrl = queryResult.data?.predictionJob?.standardizedOutputDownloadUrl;

  return (
    <div className={css(styles.outerContainer)}>
      <div className={css(styles.content)}>
        <div className={css(styles.contentPadding)}>
          <div className={css(styles.buttonContainer)}>
            <Button
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              size={ButtonSize.SMALL}
              icon="chevron-left"
              text="View all runs"
              onClick={navigateToList}
            />

            <StructurePredictionInfo
              modelDisplayName="BoltzGen"
              modelVersion="0.1.2"
              className={css(styles.predictionInfo)}
            />
            <DownloadZippedResultButton run={run} />
          </div>
        </div>
        <StructureFileViewer signedUrl={signedUrl} loading={queryResult.type === QueryResultType.PENDING} />
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  outerContainer: STICKY_FOOTER_CONTAINER_STYLES,
  buttonContainer: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  content: {
    ...STICKY_FOOTER_CONTENT_STYLES,
    display: 'flex',
    flexDirection: 'column',
  },
  contentPadding: {
    padding: '20px',
    flexShrink: 0,
  },
  viewer: {
    borderWidth: '0px',
    flex: '1 1 auto',
  },
  viewerWrapper: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 0%',
    minHeight: 0,
  },
  predictionInfo: {
    margin: '0',
  },
});
